import { FC, PropsWithChildren, ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import EToast from '@/components/everridge/EToast';
import { Button } from '@/components/ui/button';
import Delete_02_Icon from '@/components/icons/Delete_02_Icon';
import CloudUploadIconDuotone from '@/components/icons/CloudUploadIconDuotone';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {
	pictureUrl?: string;
	onClose: () => void;
	isMobile?: boolean;
};

const EChangeProfilePictureModal: FC<PropsWithChildren<Props>> = ({
	pictureUrl,
	onClose,
	isMobile,
}) => {
	const [avatar, setAvatar] = useState<File | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [isOpen, setIsOpen] = useState(true);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	const axiosInstance = useAxiosAuth();

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const { mutateAsync: changeProfilePicture, isPending: isPendingChange } =
		useMutation({
			mutationFn: async (dto: File) => {
				const formData = new FormData();
				formData.append('file', dto);

				const url =
					ApiConfig.queries.features.identity.endpoints.changeAvatar;
				const response = await axiosInstance.put(url, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
				return response.data;
			},
			onSuccess: async () => {
				EToast({
					variant: 'success',
					message: 'Photo added successfully.',
				});
				await queryClient.invalidateQueries({
					queryKey: [
						ApiConfig.queries.features.accounts.queryKeys.myProfile,
					],
				});
			},
			onError: () => {
				EToast({ variant: 'error', message: 'Error adding photo.' });
			},
		});

	const { mutateAsync: removeProfilePicture, isPending: isPendingRemove } =
		useMutation({
			mutationFn: async () => {
				const url =
					ApiConfig.queries.features.identity.endpoints.removeAvatar;
				const response = await axiosInstance.delete(url);
				return response.data;
			},
			onSuccess: async () => {
				EToast({
					variant: 'success',
					message: 'Photo removed successfully.',
				});
				await queryClient.invalidateQueries({
					queryKey: [
						ApiConfig.queries.features.accounts.queryKeys.myProfile,
					],
				});
			},
			onError: () => {
				EToast({ variant: 'error', message: 'Error removing photo.' });
			},
		});

	const changeFileHandler = async (file: File) => {
		if (file) {
			setAvatar(file);
		}
	};

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			changeFileHandler(file);
		}
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(true);
	};

	const handleDragLeave = () => {
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();

		const file = e.dataTransfer.files?.[0];
		if (file) {
			changeFileHandler(file);
		}
	};

	const removeProfilePictureHandler = async () => {
		await removeProfilePicture();
		handleClose();
	};

	const submitFormHandler = async () => {
		if (avatar) {
			await changeProfilePicture(avatar);
			handleClose();
		}
	};

	const inputRefHandler = () => {
		if (inputRef.current) inputRef.current.click();
	};

	const newPictureFooterRender = (): ReactElement => {
		return (
			<>
				<Button
					variant='outline'
					className={'text-error-1000'}
					onClick={handleClose}>
					{t('Cancel')}
				</Button>
				<Button
					disabled={!avatar}
					onClick={submitFormHandler}>
					{t('Save changes')}
				</Button>
			</>
		);
	};

	const changePictureFooterRender = (): ReactElement => {
		return (
			<>
				<Button
					variant='outline'
					className={cn(
						!(pictureUrl || avatar) ? '' : 'text-error-1000',
					)}
					disabled={!(pictureUrl || avatar)}
					onClick={removeProfilePictureHandler}>
					<Delete_02_Icon
						className={cn(
							!(pictureUrl || avatar)
								? 'stroke-popover-400'
								: 'stroke-error-1000',
						)}
					/>
					{t('Remove')}
				</Button>
				<Button
					disabled={!avatar}
					onClick={submitFormHandler}>
					{t('Upload new')}
				</Button>
			</>
		);
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogDescription />
					<DialogTitle>
						{t('Profile picture')}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
				</DialogHeader>
				<div
					className={cn(
						'size-60 mx-auto flex flex-col items-center justify-center gap-2 bg-popover-10 border border-dashed border-popover-100 rounded-full cursor-pointer select-none overflow-hidden transition-all hover:bg-primary-color-50',
						isMobile && 'size-full',
						isDragging ? 'bg-popover-50' : '',
					)}
					onClick={pictureUrl ? undefined : inputRefHandler}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}>
					{pictureUrl || avatar ? (
						<img
							src={
								avatar
									? URL.createObjectURL(avatar)
									: `${ApiConfig.currentUrls.api.slice(
											0,
											-1,
									  )}${pictureUrl}`
							}
							alt='Profile picture'
							className='w-full h-full object-cover'
							onClick={inputRefHandler}
						/>
					) : (
						<>
							<CloudUploadIconDuotone className='size-8' />
							<div className='flex flex-col items-center gap-1'>
								<p className='text-sm font-medium'>
									{t('Drop photo here or')}{' '}
									<span className='underline'>
										{t('Browse')}
									</span>
								</p>
								<p className='text-sm text-popover-600'>
									{t('Max. file size 10MB')}
								</p>
							</div>
						</>
					)}
				</div>
				<input
					type='file'
					className={'hidden'}
					ref={inputRef}
					onChange={handleFileInputChange}
				/>
				<DialogFooter
					className={cn(
						'justify-between',
						isMobile && 'justify-end mt-auto',
					)}>
					{pictureUrl
						? changePictureFooterRender()
						: newPictureFooterRender()}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default EChangeProfilePictureModal;
